import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TablaIcon from '../components/TablaIcon';
import t from '../theme';

export default function ClassDetailPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [classData,     setClassData]     = useState(null);
  const [taals,         setTaals]         = useState([]);
  const [types,         setTypes]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedTaal,  setSelectedTaal]  = useState(null);
  const [selectedType,  setSelectedType]  = useState(null);
  const [lessonModal,   setLessonModal]   = useState(false);
  const [lessonForm,    setLessonForm]    = useState({ title:'', taal_id:'', type_id:'', notation:'' });
  const [lessonError,   setLessonError]   = useState('');
  const [lessonSaving,  setLessonSaving]  = useState(false);
  const [deleteLesson,  setDeleteLesson]  = useState(null);
  const [comment,       setComment]       = useState('');
  const [commenting,    setCommenting]    = useState(false);

  const fetchClass = useCallback(async () => {
    try {
      const [cr, tr, tyr] = await Promise.all([
        axios.get(`/api/classes/${classId}`),
        axios.get('/api/taals'),
        axios.get('/api/types'),
      ]);
      setClassData(cr.data); setTaals(tr.data); setTypes(tyr.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [classId]);

  useEffect(() => { fetchClass(); }, [fetchClass]);

  const taalIdsWithLessons = [...new Set((classData?.lessonItems||[]).map(l=>l.taal_id))];
  const activeTaals = taals.filter(taal => isAdmin || taalIdsWithLessons.includes(taal.id));

  const typeIdsForTaal = [...new Set(
    (classData?.lessonItems||[]).filter(l=>l.taal_id===selectedTaal?.id).map(l=>l.type_id)
  )];
  const activeTypes = types.filter(type => isAdmin || typeIdsForTaal.includes(type.id));

  const filteredLessons = (classData?.lessonItems||[]).filter(
    l => l.taal_id===selectedTaal?.id && l.type_id===selectedType?.id
  );

  const openLessonModal = () => {
    setLessonForm({ title:'', taal_id: selectedTaal?.id||'', type_id: selectedType?.id||'', notation:'' });
    setLessonError(''); setLessonModal(true);
  };

  const handleSaveLesson = async () => {
    setLessonError('');
    if (!lessonForm.title.trim()) return setLessonError('Title is required.');
    if (!lessonForm.taal_id)      return setLessonError('Please select a Taal.');
    if (!lessonForm.type_id)      return setLessonError('Please select a Type.');
    setLessonSaving(true);
    try {
      await axios.post(`/api/classes/${classId}/lessons`, lessonForm);
      setLessonModal(false); fetchClass();
    } catch (e) { setLessonError(e.response?.data?.error || 'Failed to save.'); }
    finally { setLessonSaving(false); }
  };

  const handleDeleteLesson = async () => {
    if (!deleteLesson) return;
    try { await axios.delete(`/api/classes/${classId}/lessons/${deleteLesson.id}`); setDeleteLesson(null); fetchClass(); }
    catch (e) { console.error(e); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setCommenting(true);
    try { await axios.post(`/api/classes/${classId}/comments`, { comment_text: comment.trim() }); setComment(''); fetchClass(); }
    catch (e) { console.error(e); } finally { setCommenting(false); }
  };

  const handleDeleteComment = async (id) => {
    try { await axios.delete(`/api/classes/${classId}/comments/${id}`); fetchClass(); }
    catch (e) { console.error(e); }
  };

  const fmtTime = d => new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});

  if (loading) return <div style={{ padding:'48px', textAlign:'center', color:t.text3 }}>Loading class...</div>;
  if (!classData) return <div style={{ padding:'48px', textAlign:'center', color:t.text3 }}>Class not found.</div>;

  return (
    <div>
      <button style={{ background:'none', border:'none', color:t.accent, fontSize:'13px', cursor:'pointer', padding:0, fontFamily:t.font, marginBottom:'16px' }}
        onClick={() => navigate('/classes')}>← Back to Classes</button>

      {/* Class header */}
      <div style={{ ...t.card, padding:'22px 26px', marginBottom:'20px', borderLeft:`3px solid ${t.accent}`,
        display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
          <TablaIcon size={36} color={t.accent}/>
          <div>
            <h2 style={{ fontSize:'20px', fontWeight:600, color:t.text, margin:'0 0 6px', letterSpacing:'-0.3px' }}>{classData.title}</h2>
            {classData.description && <p style={{ fontSize:'13px', color:t.text3, margin:'0 0 8px' }}>{classData.description}</p>}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
              {classData.students?.map(st => (
                <span key={st.id} style={{ background:t.surface2, border:`1px solid ${t.border}`, color:t.text2,
                  padding:'2px 10px', borderRadius:'20px', fontSize:'12px' }}>{st.full_name}</span>
              ))}
            </div>
          </div>
        </div>
        {isAdmin && <button style={t.btnPrimary} onClick={openLessonModal}>+ Add Lesson</button>}
      </div>

      {/* Tree + comments layout */}
      <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>

        {/* Three-column lesson tree */}
        <div style={{ display:'flex', gap:'12px', alignItems:'flex-start', flexWrap:'wrap' }}>

          {/* Taal column */}
          <div style={{ ...t.card, flex:1, minWidth:'180px', overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px',
              borderBottom:`1px solid ${t.border}`, background:t.surface2 }}>
              <TablaIcon size={16} color={t.accent}/>
              <span style={{ fontSize:'12px', fontWeight:600, color:t.text, flex:1 }}>Select Taal</span>
            </div>
            <div style={{ padding:'6px' }}>
              {activeTaals.length === 0
                ? <div style={{ padding:'18px', textAlign:'center', color:t.text3, fontSize:'12px' }}>
                    {isAdmin ? 'Add a lesson to start.' : 'No lessons yet.'}
                  </div>
                : activeTaals.map(taal => {
                  const count = (classData?.lessonItems||[]).filter(l=>l.taal_id===taal.id).length;
                  const active = selectedTaal?.id === taal.id;
                  return (
                    <button key={taal.id} onClick={() => { setSelectedTaal(taal); setSelectedType(null); }}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        width:'100%', padding:'9px 11px', borderRadius:'6px', border:'none',
                        background: active ? t.accent : 'none',
                        color: active ? '#0e0e0d' : t.text2,
                        fontFamily:t.font, fontSize:'13px', fontWeight: active ? 600 : 400,
                        cursor:'pointer', marginBottom:'1px', textAlign:'left' }}>
                      <span>{taal.name}</span>
                      <span style={{ fontSize:'11px', background: active ? 'rgba(0,0,0,0.15)' : t.surface2,
                        color: active ? '#0e0e0d' : t.text3,
                        padding:'1px 7px', borderRadius:'20px', fontWeight:600 }}>{count}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Type column */}
          <div style={{ ...t.card, flex:1, minWidth:'180px', overflow:'hidden', opacity: selectedTaal ? 1 : 0.45 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px',
              borderBottom:`1px solid ${t.border}`, background:t.surface2 }}>
              <span style={{ fontSize:'13px', color:t.accent, fontFamily:'monospace' }}>◈</span>
              <span style={{ fontSize:'12px', fontWeight:600, color:t.text, flex:1 }}>
                {selectedTaal ? `Types · ${selectedTaal.name}` : 'Select a Taal first'}
              </span>
            </div>
            <div style={{ padding:'6px' }}>
              {!selectedTaal
                ? <div style={{ padding:'18px', textAlign:'center', color:t.text3, fontSize:'12px' }}>← Choose a Taal</div>
                : activeTypes.length === 0
                  ? <div style={{ padding:'18px', textAlign:'center', color:t.text3, fontSize:'12px' }}>No types yet.</div>
                  : activeTypes.map(type => {
                    const count = (classData?.lessonItems||[]).filter(l=>l.taal_id===selectedTaal?.id && l.type_id===type.id).length;
                    const active = selectedType?.id === type.id;
                    return (
                      <button key={type.id} onClick={() => setSelectedType(type)}
                        style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          width:'100%', padding:'9px 11px', borderRadius:'6px', border:'none',
                          background: active ? t.accent : 'none',
                          color: active ? '#0e0e0d' : t.text2,
                          fontFamily:t.font, fontSize:'13px', fontWeight: active ? 600 : 400,
                          cursor:'pointer', marginBottom:'1px', textAlign:'left' }}>
                        <span>{type.name}</span>
                        <span style={{ fontSize:'11px', background: active ? 'rgba(0,0,0,0.15)' : t.surface2,
                          color: active ? '#0e0e0d' : t.text3,
                          padding:'1px 7px', borderRadius:'20px', fontWeight:600 }}>{count}</span>
                      </button>
                    );
                  })}
            </div>
          </div>

          {/* Lessons column */}
          <div style={{ ...t.card, flex:2, minWidth:'220px', overflow:'hidden', opacity: selectedType ? 1 : 0.45 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px',
              borderBottom:`1px solid ${t.border}`, background:t.surface2 }}>
              <span style={{ fontSize:'13px', color:t.accent, fontFamily:'monospace' }}>◆</span>
              <span style={{ fontSize:'12px', fontWeight:600, color:t.text, flex:1 }}>
                {selectedType && selectedTaal ? `${selectedTaal.name} — ${selectedType.name}` : 'Select a Type'}
              </span>
              {isAdmin && selectedTaal && selectedType && (
                <button style={{ ...t.btnPrimary, padding:'3px 10px', fontSize:'11px' }} onClick={openLessonModal}>+ Add</button>
              )}
            </div>
            <div style={{ padding:'8px', display:'flex', flexDirection:'column', gap:'5px' }}>
              {!selectedType
                ? <div style={{ padding:'18px', textAlign:'center', color:t.text3, fontSize:'12px' }}>← Choose a Type</div>
                : filteredLessons.length === 0
                  ? <div style={{ padding:'18px', textAlign:'center', color:t.text3, fontSize:'12px' }}>
                      {isAdmin ? `No ${selectedType.name}s yet. Click "+ Add".` : 'No lessons here yet.'}
                    </div>
                  : filteredLessons.map((lesson, idx) => (
                    <div key={lesson.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 12px', borderRadius:'8px', background:t.surface2, border:`1px solid ${t.border}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'10px', flex:1, minWidth:0 }}>
                        <span style={{ width:'22px', height:'22px', borderRadius:'50%', background:t.accentLight,
                          border:`1px solid ${t.accentBorder}`, display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:'11px', fontWeight:600, color:t.accent, flexShrink:0 }}>
                          {idx+1}
                        </span>
                        <div>
                          <div style={{ fontSize:'13px', fontWeight:500, color:t.text }}>{lesson.title}</div>
                          <div style={{ display:'flex', gap:'4px', marginTop:'2px' }}>
                            {lesson.video_url && <span style={{ fontSize:'11px', background:t.surface3, color:t.text3, padding:'1px 7px', borderRadius:'4px' }}>Media</span>}
                            {lesson.notation && <span style={{ fontSize:'11px', background:t.surface3, color:t.text3, padding:'1px 7px', borderRadius:'4px' }}>Notes</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
                        {isAdmin && (
                          <button style={{ ...t.btnDanger, padding:'4px 8px', fontSize:'12px' }}
                            onClick={() => setDeleteLesson(lesson)}>Del</button>
                        )}
                        <button style={{ ...t.btnPrimary, padding:'5px 10px', fontSize:'12px' }}
                          onClick={() => navigate(`/classes/${classId}/lessons/${lesson.id}`)}>Open →</button>
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* Class comments */}
        <div style={{ ...t.card, padding:'20px' }}>
          <div style={{ fontSize:'11px', fontWeight:600, color:t.text3, textTransform:'uppercase', letterSpacing:'0.6px', marginBottom:'14px' }}>
            Class Comments · {classData.comments?.length || 0}
          </div>

          {/* Input */}
          <div style={{ display:'flex', gap:'10px', marginBottom:'16px' }}>
            <div style={{ width:'30px', height:'30px', borderRadius:'6px', background:t.accentLight,
              border:`1px solid ${t.accentBorder}`, display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'12px', fontWeight:600, color:t.accent, flexShrink:0, marginTop:'2px' }}>
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'8px' }}>
              <textarea style={{ ...t.textarea, resize:'vertical' }} rows={2}
                placeholder="Ask a question or leave a note..." value={comment}
                onChange={e => setComment(e.target.value)}/>
              <button style={{ ...t.btnPrimary, alignSelf:'flex-end', padding:'7px 16px', fontSize:'12px' }}
                onClick={handleComment} disabled={commenting || !comment.trim()}>
                {commenting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>

          {/* Comments list */}
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {!classData.comments?.length
              ? <div style={{ color:t.text3, fontSize:'13px', fontStyle:'italic' }}>No comments yet.</div>
              : [...classData.comments].reverse().map(c => (
                <div key={c.id} style={{ display:'flex', gap:'10px' }}>
                  <div style={{ width:'28px', height:'28px', borderRadius:'6px', flexShrink:0,
                    background: c.author?.role==='admin' ? 'rgba(196,154,60,0.2)' : t.surface3,
                    border:`1px solid ${c.author?.role==='admin' ? t.accentBorder : t.border}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'11px', fontWeight:600, color: c.author?.role==='admin' ? t.accent : t.text2 }}>
                    {c.author?.username?.[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex:1, background:t.surface2, borderRadius:'8px', padding:'10px 12px', border:`1px solid ${t.border}` }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}>
                      <span style={{ fontSize:'12px', fontWeight:500, color:t.text }}>{c.author?.username}</span>
                      {c.author?.role==='admin' && (
                        <span style={{ fontSize:'10px', background:t.accentLight, color:t.accent, padding:'1px 6px', borderRadius:'4px', fontWeight:600 }}>Teacher</span>
                      )}
                      <span style={{ fontSize:'11px', color:t.text3, marginLeft:'auto' }}>{fmtTime(c.createdAt)}</span>
                    </div>
                    <p style={{ fontSize:'13px', color:t.text2, margin:0, lineHeight:1.6, whiteSpace:'pre-wrap' }}>{c.comment_text}</p>
                    {(isAdmin || c.user_id === user?.id) && (
                      <button style={{ background:'none', border:'none', color:t.red, fontSize:'11px', cursor:'pointer', fontFamily:t.font, marginTop:'5px', padding:0 }}
                        onClick={() => handleDeleteComment(c.id)}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Add Lesson Modal */}
      {lessonModal && (
        <div style={t.overlay} onClick={e => e.target===e.currentTarget && setLessonModal(false)}>
          <div style={t.modal}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>Add Lesson</h3>
              <button style={t.closeBtn} onClick={() => setLessonModal(false)}>✕</button>
            </div>
            <div style={t.modalBody}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' }}>
                <div style={{ ...t.fieldGroup, gridColumn:'1/-1' }}>
                  <label style={t.label}>Title *</label>
                  <input style={t.input} value={lessonForm.title}
                    onChange={e => setLessonForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Kaida #1"/>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Taal *</label>
                  <select style={t.select} value={lessonForm.taal_id}
                    onChange={e => setLessonForm(f=>({...f,taal_id:e.target.value}))}>
                    <option value="">— Select —</option>
                    {taals.map(tl => <option key={tl.id} value={tl.id}>{tl.name}</option>)}
                  </select>
                </div>
                <div style={t.fieldGroup}>
                  <label style={t.label}>Type *</label>
                  <select style={t.select} value={lessonForm.type_id}
                    onChange={e => setLessonForm(f=>({...f,type_id:e.target.value}))}>
                    <option value="">— Select —</option>
                    {types.map(tp => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={t.fieldGroup}>
                <label style={t.label}>Notation (optional)</label>
                <textarea style={t.textarea} rows={3} value={lessonForm.notation}
                  onChange={e => setLessonForm(f=>({...f,notation:e.target.value}))} placeholder="Notes or notation..."/>
              </div>
              {lessonError && <div style={t.errorBox}>{lessonError}</div>}
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setLessonModal(false)}>Cancel</button>
              <button style={t.btnPrimary} onClick={handleSaveLesson} disabled={lessonSaving}>
                {lessonSaving ? 'Saving...' : 'Add Lesson'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Lesson Modal */}
      {deleteLesson && (
        <div style={t.overlay} onClick={e => e.target===e.currentTarget && setDeleteLesson(null)}>
          <div style={{ ...t.modal, maxWidth:'400px' }}>
            <div style={t.modalHeader}>
              <h3 style={t.modalTitle}>Delete Lesson</h3>
              <button style={t.closeBtn} onClick={() => setDeleteLesson(null)}>✕</button>
            </div>
            <div style={{ ...t.modalBody, textAlign:'center' }}>
              <p style={{ color:t.text2 }}>Delete <strong style={{ color:t.text }}>{deleteLesson.title}</strong>? This cannot be undone.</p>
            </div>
            <div style={t.modalFooter}>
              <button style={t.btnSecondary} onClick={() => setDeleteLesson(null)}>Cancel</button>
              <button style={t.btnDanger} onClick={handleDeleteLesson}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
